import { Star, Quote } from "lucide-react";
import { useLang } from "@/i18n";

const SCORES = [
  { key: "facilities", value: 9.5 },
  { key: "cleanliness", value: 9.8 },
  { key: "comfort", value: 9.8 },
  { key: "value", value: 9.7 },
  { key: "location", value: 9.4 },
  { key: "wifi", value: 10.0 },
];

const QUOTES = {
  pl: [
    { name: "Andrea", country: "Chorwacja", text: "Wszystko — super czysto. W pełni wyposażona kuchnia i łazienka. Niesamowity taras. Ładne meble i dwie łazienki." },
    { name: "Olga", country: "Hiszpania", text: "Domek jest piękny, nowy, ma wszystko, czego potrzeba na kilka dni. Salon i taras są bardzo wygodne i przytulne. Wygodne łóżka, dwie dobrze wyposażone łazienki." },
    { name: "Callum", country: "Wielka Brytania", text: "Dom był świetny, mnóstwo miejsca dla naszej pięcioosobowej rodziny z trójką dzieci. Fantastyczne widoki, a obiekt jest pięknie wykończony." },
    { name: "Richmond", country: "Polska", text: "Widok jest fantastyczny i można się nim wygodnie cieszyć z dużych balkonów. Kuchnia miała wszystko, czego potrzebowaliśmy. Miłym dodatkiem była kolekcja książek." },
    { name: "Sylwia", country: "Polska", text: "Całokształt — to jak czysto było, widok z okna, wystrojenie domku." },
    { name: "Crz", country: "Polska", text: "Wszystko, śliczny domek, lokalizacja, widok z okna." },
  ],
  en: [
    { name: "Andrea", country: "Croatia", text: "Everything — super clean. Fully equipped kitchen & bathroom. Amazing terrace. Nice furniture and two bathrooms." },
    { name: "Olga", country: "Spain", text: "The house is beautiful, new, has everything you need for a few days' stay. The living room and terrace are very comfortable and cosy. Comfortable beds. Two well-equipped bathrooms." },
    { name: "Callum", country: "United Kingdom", text: "The house itself was great, lots of space for our family of five with three children. Fantastic views and the property was finished beautifully." },
    { name: "Richmond", country: "Poland", text: "The view is fantastic and you can enjoy it comfortably from the large balconies. The kitchen had everything we needed. The collection of books was a nice addition." },
    { name: "Inho", country: "South Korea", text: "The location and facilities were very convenient, and the view of the mountains was great." },
    { name: "Mohammed", country: "Saudi Arabia", text: "A beautiful, lovely cottage, everything is provided and close to Zakopane. Wonderful view — exactly like the photos from the balcony." },
  ],
};

export default function Reviews() {
  const { lang, t } = useLang();
  const quotes = QUOTES[lang];
  return (
    <section id="opinie" className="py-24 lg:py-32 bg-[#efe6d3]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.35em] uppercase text-[#8a6135]" data-testid="reviews-eyebrow">
              {t.reviews.eyebrow}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2a1d12] mt-3 leading-tight">
              {t.reviews.title[0]}<span className="italic">{t.reviews.title[1]}</span>
            </h2>
            <p className="mt-5 text-[#3b2a1c]/80 text-[15px] leading-relaxed">{t.reviews.sub}</p>

            <div className="mt-8 p-6 rounded-2xl bg-white border border-[#2a1d12]/10 flex items-center gap-5">
              <div className="text-center">
                <div className="font-serif text-5xl text-[#2a1d12] leading-none">9.5</div>
                <div className="mt-1 flex items-center justify-center gap-0.5 text-[#c89b6a]">
                  {[0,1,2,3,4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-[#c89b6a]" />)}
                </div>
              </div>
              <div className="border-l border-[#2a1d12]/10 pl-5">
                <div className="font-serif text-xl text-[#2a1d12]">{t.reviews.scoreLabel}</div>
                <div className="text-sm text-[#3b2a1c]/75 mt-1">{t.reviews.basedOn}</div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
              {SCORES.map((s) => (
                <div key={s.key} data-testid={`review-score-${s.key}`} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-white/60">
                  <span className="text-[#3b2a1c]/80">{t.reviews.cats[s.key]}</span>
                  <span className="font-serif text-[#2a1d12]">{s.value.toFixed(1)}</span>
                </div>
              ))}
            </div>

            <a
              href="https://www.booking.com/hotel/pl/domek-jatka-no-1.html"
              target="_blank"
              rel="noreferrer"
              data-testid="reviews-source-link"
              className="mt-6 inline-flex text-sm text-[#8a6135] hover:text-[#2a1d12] transition-colors"
            >
              {t.reviews.source}
            </a>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {quotes.map((q, idx) => (
              <figure
                key={idx}
                data-testid={`review-quote-${idx}`}
                className="p-6 rounded-2xl bg-white border border-[#2a1d12]/10 hover:border-[#c89b6a]/60 hover:-translate-y-1 transition-all duration-300"
              >
                <Quote className="w-5 h-5 text-[#c89b6a] mb-3" />
                <blockquote className="text-[#3b2a1c] text-[14.5px] leading-[1.75]">&ldquo;{q.text}&rdquo;</blockquote>
                <figcaption className="mt-4 pt-3 border-t border-[#2a1d12]/8 flex items-center justify-between text-xs">
                  <span className="font-medium text-[#2a1d12]">{q.name}</span>
                  <span className="text-[#3b2a1c]/60">{q.country}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
